import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isOwnerOrAdmin } from "@/lib/permissions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ClipboardEdit, Lock, Camera, FileSignature, User2, X, Loader2, Link2, MapPin, User as UserIcon } from "lucide-react";

// Updated Feb 2026: Informed Consent is now STRICTLY OPTIONAL for
// every service and category (Global Validation Update). Session
// Notes remain the ONLY hard requirement — every attachment slot
// is a nice-to-have the practitioner can skip.
const ATTACHMENT_KINDS = [
  { key: "face_chart", label: "Face Chart", Icon: User2, testid: "medical-upload-face-chart", required: false },
  { key: "photo", label: "Photo", Icon: Camera, testid: "medical-upload-photo", required: false },
  { key: "informed_consent", label: "Informed Consent", Icon: FileSignature, testid: "medical-upload-consent", required: false },
];

// Iter-333 (Feb 2026) · Photo capacity raised from 12 → 20 per record.
// Client-side gate ONLY blocks new uploads once the array is at capacity.
// It NEVER prunes or overwrites existing attachments — the ceiling is
// applied to `attachments.length` before push, and existing rows fetched
// from the backend hydrate untouched. Mirror of the backend cap at
// `MAX_MED_ATTACHMENTS_PER_RECORD = 20`. Global scope · all 8 branches.
const MAX_ATTACHMENTS_PER_RECORD = 20;

const MAX_KB = 150;
const MAX_BYTES = MAX_KB * 1024;

/**
 * Compress an image File to a JPEG data URL <= MAX_BYTES.
 *
 * Draws the image onto a canvas and iteratively lowers quality (and
 * finally the pixel scale) until the encoded base64 fits under the
 * 150KB ceiling. Nothing is watermarked — the compressed image is
 * the raw treatment photo, just smaller.
 */
async function compressImageToDataUrl(file) {
  const img = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Could not decode image"));
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  let scale = 1.0;
  // Cap dimensions early — a 4000×3000 phone shot compresses much
  // faster once resampled to ≤1600px on the long edge.
  const MAX_DIM = 1600;
  const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
  if (longEdge > MAX_DIM) scale = MAX_DIM / longEdge;

  let quality = 0.82;
  let dataUrl = "";
  for (let attempt = 0; attempt < 12; attempt++) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    dataUrl = canvas.toDataURL("image/jpeg", quality);
    // data URL bytes ≈ base64 length * 3/4 (minus header, negligible)
    const b64 = dataUrl.split(",", 2)[1] || "";
    const bytes = Math.floor(b64.length * 3 / 4);
    if (bytes <= MAX_BYTES) return dataUrl;
    if (quality > 0.4) quality -= 0.12;
    else scale *= 0.85;
  }
  return dataUrl;
}

const kindLabel = (k) => ATTACHMENT_KINDS.find((x) => x.key === k)?.label || k;

/**
 * Per-visit medical record dialog. Reads & writes
 * `/api/appointments/{aid}/medical-record`. Two sections:
 *   1. Session Notes (Public) — visible on the client's own Medical
 *      History (portal). Includes 3 upload slots (Face Chart /
 *      Photo / Informed Consent) auto-compressed to 150KB.
 *   2. Internal Notes (Private) — strictly Care Team + Management,
 *      NEVER exposed on any guest-facing endpoint.
 */
export default function MedicalRecordDialog({ open, onOpenChange, appt }) {
  const { staff } = useAuth();
  const canOverride = isOwnerOrAdmin(staff);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [compressing, setCompressing] = useState(null); // kind key being processed
  // Iter-193 · Conditional Mandatory Upload for Clinical Records.
  // Mirror of the backend rule at /appointments/{aid}/consent-requirement:
  // Consent is mandatory unless every service in the session is a
  // Consultation. Start in the strict "required" state so a slow
  // network can never unlock Save prematurely.
  const [consentRequired, setConsentRequired] = useState(true);
  const [sessionServices, setSessionServices] = useState([]);
  // Iter-287 · Linked-Visit Consolidation. When the primary
  // appointment shares a `link_group_id` or `package_id` with
  // siblings, the record is a shared session record; the dialog
  // surfaces every treatment (service · specialist · branch)
  // right at the top so the practitioner reads the full itinerary
  // before entering notes.
  const [isLinkedSession, setIsLinkedSession] = useState(false);
  const [record, setRecord] = useState({
    session_notes: "",
    internal_notes: "",
    attachments: [],
  });

  useEffect(() => {
    if (!open || !appt?.id) return;
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const [r, cr] = await Promise.all([
          staffApi.get(`/appointments/${appt.id}/medical-record`),
          staffApi.get(`/appointments/${appt.id}/consent-requirement`),
        ]);
        if (!mounted) return;
        setRecord({
          session_notes: r.data?.session_notes || r.data?.notes || "",
          internal_notes: r.data?.internal_notes || "",
          attachments: Array.isArray(r.data?.attachments) ? r.data.attachments : [],
        });
        setConsentRequired(cr.data?.consent_required !== false);
        setSessionServices(Array.isArray(cr.data?.session_services) ? cr.data.session_services : []);
        setIsLinkedSession(!!cr.data?.is_linked);
      } catch (e) {
        if (mounted) toast.error(formatApiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, appt?.id]);

  const pickAndUpload = async (kind) => {
    if (compressing) return;
    // Iter-333 · Photo capacity gate. Refuse a new upload once the
    // record already holds the maximum. Existing attachments are
    // preserved untouched — the guard only blocks NEW additions.
    if ((record.attachments || []).length >= MAX_ATTACHMENTS_PER_RECORD) {
      toast.error(`At most ${MAX_ATTACHMENTS_PER_RECORD} photos per record. Remove one to add another.`);
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp";
    // Mobile browsers will show a native "Camera / Photo Library"
    // sheet — no separate handling required.
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Only image files are supported.");
        return;
      }
      setCompressing(kind);
      try {
        const dataUrl = await compressImageToDataUrl(file);
        const b64 = dataUrl.split(",", 2)[1] || "";
        const bytes = Math.floor(b64.length * 3 / 4);
        if (bytes > MAX_BYTES) {
          toast.error(`Could not compress under ${MAX_KB}KB — try a smaller photo.`);
          return;
        }
        setRecord((prev) => ({
          ...prev,
          attachments: [
            ...(prev.attachments || []),
            {
              kind,
              data_url: dataUrl,
              mime: "image/jpeg",
              size: bytes,
              _new: true,
            },
          ],
        }));
        toast.success(`${kindLabel(kind)} ready · ${(bytes / 1024).toFixed(0)}KB`);
      } catch (err) {
        toast.error(err?.message || "Could not process that image.");
      } finally {
        setCompressing(null);
      }
    };
    input.click();
  };

  const removeAttachment = (idx) => {
    setRecord((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, i) => i !== idx),
    }));
  };

  // Medical History Lockdown (Feb 2026, revised Oct 2026): new
  // records may only be created once the guest has been checked in.
  // The same-day constraint has been dropped — a care team member
  // who couldn't document the session on the day (e.g. end-of-day
  // backlog, next-day catch-up) can still fill the record for any
  // ATTENDED past visit. Mirror of the backend rule at
  // `PUT /appointments/{aid}/medical-record`.
  const isCheckedIn = !!appt?.checked_in_at;
  const apptLocalDate = (() => {
    if (!appt?.start_iso) return "";
    try {
      return new Date(appt.start_iso).toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
    } catch { return ""; }
  })();
  const todayLocal = (() => {
    try {
      return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
    } catch { return ""; }
  })();
  const isTodayAppt = apptLocalDate && todayLocal && apptLocalDate === todayLocal;
  const isPastAppt = apptLocalDate && todayLocal && apptLocalDate < todayLocal;
  // Iter-340 (Feb 2026) · Cancelled + Locked-Out Graceful Exit —
  // revised Oct 2026 so the "Dismiss / Clear Task" affordance is
  // reserved for OWNER + ADMIN logins only. Care team members never
  // see the Dismiss button; their lockdown banner stays passive
  // (read-only hint). Owner / Admin get the same contextual Dismiss
  // button inside this dialog so they can override a stuck task
  // without hunting through another surface.
  const apptStatus = String(appt?.status || "").toLowerCase();
  const isCancelled = apptStatus === "cancelled" || apptStatus === "no_show";
  // Existing record = row loaded from the server carries content or
  // an attachment. Empty create-mode is when nothing has ever been
  // saved for this appointment yet.
  const hasExistingRecord = (
    (record.session_notes || "").trim().length > 0 ||
    (record.internal_notes || "").trim().length > 0 ||
    (record.attachments || []).length > 0
  );
  // Raw lockdown predicate — mirror of the backend gate at
  // `PUT /appointments/{aid}/medical-record`: a brand-new record may
  // only be created once the guest has been checked in. The same-day
  // requirement is intentionally removed so a past ATTENDED visit
  // (`checked_in_at` present) stays retroactively fillable.
  const rawLocked = !hasExistingRecord && !isCheckedIn;
  // Owner / Admin always get the Dismiss escape hatch for cancelled
  // or stuck appointments. Care team users never see it — they rely
  // on their manager to clear the task.
  const canDismiss = canOverride && (isCancelled || rawLocked);
  // The Lockdown banner now covers the case where a CARE TEAM user
  // is blocked (no Dismiss button to swap in). Owner / Admin keep
  // the swap-in behaviour from before.
  const lockedOut = rawLocked && !canOverride;

  // Mandatory-fields gate — Session Notes are the ONLY required
  // field on the medical record now (Feb 2026 Global Validation
  // Update made Informed Consent strictly optional). Every
  // attachment slot is optional so the Save button unlocks the
  // moment notes are typed.
  const hasKind = (k) => (record.attachments || []).some((a) => a?.kind === k);
  const notesFilled = (record.session_notes || "").trim().length > 0;
  const missing = [];
  if (!notesFilled) missing.push("Session notes");
  const canSave = missing.length === 0 && !loading && !saving && !compressing && !lockedOut && !canDismiss;

  // Iter-340 · Dismiss handler for cancelled appointments. Fires the
  // existing DELETE endpoint (which already gates on
  // `brand.customers.delete`, cascades to linked siblings, and
  // broadcasts `appointment_updated`), then closes the dialog.
  const dismissTask = async () => {
    if (!appt?.id) return;
    setDismissing(true);
    try {
      await staffApi.post(`/appointments/${appt.id}/medical-record/dismiss`);
      toast.success("Task cleared");
      try {
        window.dispatchEvent(new CustomEvent("medical-record-saved", {
          detail: { appointment_id: appt.id, dismissed: true },
        }));
      } catch (_e) { /* older browsers — ignore */ }
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDismissing(false);
    }
  };

  const save = async () => {
    if (!appt?.id) return;
    if (missing.length > 0) {
      toast.error(`Missing required: ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      await staffApi.put(`/appointments/${appt.id}/medical-record`, {
        session_notes: record.session_notes,
        internal_notes: record.internal_notes,
        attachments: record.attachments || [],
      });
      toast.success("Medical record saved");
      // Bug fix — notify any mounted roster / worklist that a record
      // was just saved so the compliance surface re-scans immediately.
      try {
        window.dispatchEvent(new CustomEvent("medical-record-saved", {
          detail: { appointment_id: appt.id },
        }));
      } catch (_e) { /* older browsers — ignore */ }
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="medical-record-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardEdit className="w-5 h-5 text-primary" />
            Medical record · {appt?.customer_name || "Guest"}
            {isLinkedSession && (
              <span
                className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5"
                data-testid="medical-record-linked-badge"
                title="Linked session — one record covers every treatment"
              >
                <Link2 className="w-3 h-3" /> Linked
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {appt?.service_name ? `${appt.service_name} · ` : ""}
            {appt?.start_iso ? new Date(appt.start_iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
          </DialogDescription>
        </DialogHeader>

        {/* Session-services roll-up. Rendered on every visit but
            visually promoted when the session covers more than one
            row — the practitioner sees every treatment, specialist,
            and branch touched by this single record. Iter-287. */}
        {sessionServices.length > 0 && (
          <section
            className={
              "mt-3 rounded-xl border p-3 " +
              (isLinkedSession
                ? "border-primary/30 bg-primary/5"
                : "border-border bg-secondary/40")
            }
            data-testid="medical-record-session-services"
          >
            <div className="flex items-center gap-1.5 mb-2">
              {isLinkedSession && <Link2 className="w-3.5 h-3.5 text-primary" />}
              <span className="text-[11px] tracking-overline uppercase font-semibold text-primary">
                {isLinkedSession
                  ? `Linked session · ${sessionServices.length} treatments`
                  : "This session"}
              </span>
            </div>
            <ul className="space-y-1.5" data-testid="medical-record-session-list">
              {sessionServices.map((s) => {
                let hm = "";
                try {
                  if (s.start_iso) {
                    hm = new Date(s.start_iso).toLocaleTimeString("en-GB", {
                      hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar",
                    });
                  }
                } catch { /* ignore */ }
                return (
                  <li
                    key={s.appointment_id}
                    className="text-xs bg-white/70 border border-border/60 rounded-md px-2.5 py-1.5"
                    data-testid={`medical-record-session-row-${s.appointment_id}`}
                  >
                    <div className="flex items-baseline gap-2">
                      {hm && (
                        <span
                          className="text-[10px] font-medium tabular-nums text-primary bg-primary/10 border border-primary/15 rounded-full px-1.5 py-0.5 shrink-0"
                        >
                          {hm}
                        </span>
                      )}
                      <span className="font-medium truncate flex-1" data-testid={`medical-record-session-service-${s.appointment_id}`}>
                        {s.service_name || "Service"}
                      </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      {(() => {
                        // Iter-346 · Render EVERY assigned care team
                        // member (primary + assists) so multi-person
                        // services (e.g. PRP with 2 practitioners)
                        // show both names, not just the primary.
                        const names = [];
                        if (s.staff_name) names.push(s.staff_name);
                        for (const n of (s.assist_staff_names || [])) {
                          if (n && !names.includes(n)) names.push(n);
                        }
                        if (names.length === 0) return null;
                        return (
                          <span
                            className="inline-flex items-center gap-1"
                            data-testid={`medical-record-session-staff-${s.appointment_id}`}
                          >
                            <UserIcon className="w-3 h-3" /> {names.join(", ")}
                          </span>
                        );
                      })()}
                      {s.branch_name && (
                        <span className="inline-flex items-center gap-1" data-testid={`medical-record-session-branch-${s.appointment_id}`}>
                          <MapPin className="w-3 h-3" /> {s.branch_name}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {isLinkedSession && (
              <p className="text-[10px] text-muted-foreground mt-2 leading-tight">
                Saving this form updates every treatment above in one go — the whole visit turns green.
              </p>
            )}
          </section>
        )}

        {/* Iter-340 · Graceful-exit banner. Owner / Admin only — the
            same block renders the Dismiss button in the footer. */}
        {canDismiss && !loading && (
          <div
            className="mt-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 px-3 py-2.5 flex items-start gap-2"
            data-testid="medical-record-cancelled-banner"
          >
            <X className="w-4 h-4 mt-0.5 shrink-0 text-slate-600" />
            <div className="text-xs leading-relaxed">
              <p className="font-semibold">
                {isCancelled
                  ? "This appointment was cancelled"
                  : "Guest never checked in — no medical session on file"}
              </p>
              <p>
                {isCancelled
                  ? "The visit never happened — Save is disabled. "
                  : "A new record can only be filed once the guest has been checked in. "}
                As an administrator you can use <strong>Dismiss / Clear Task</strong> below to
                remove this pending medical-history task from the
                specialist's roster.
              </p>
            </div>
          </div>
        )}

        {/* Medical History Lockdown banner (Feb 2026, revised Oct
            2026). Shown to CARE TEAM users when the guest has not
            been checked in yet — the lockdown is passive, no
            Dismiss button is offered. Owner / Admin get the swap-in
            banner above (with Dismiss) instead. */}
        {lockedOut && !loading && (
          <div
            className="mt-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-900 px-3 py-2.5 flex items-start gap-2"
            data-testid="medical-record-lockdown-banner"
          >
            <Lock className="w-4 h-4 mt-0.5 shrink-0 text-rose-700" />
            <div className="text-xs leading-relaxed">
              <p className="font-semibold">Medical History Lockdown</p>
              <p>
                New medical records can only be created once the
                guest has been checked in. If this looks wrong, ask
                an administrator to review or dismiss the task.
              </p>
            </div>
          </div>
        )}

        {/* Section 1 — Public Session Notes + Uploads. Everything in
            this block will appear on the guest's own Medical History
            in the customer portal. */}
        <section
          className="mt-3 rounded-xl border border-border bg-white p-4 space-y-3"
          data-testid="medical-record-section-public"
        >
          <div className="flex items-center gap-2">
            <span className="text-[11px] tracking-overline text-emerald-700 uppercase font-semibold">Session notes</span>
            <span className="text-[10px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">Visible to client</span>
            <span className="text-[10px] rounded-full bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 font-semibold">Required</span>
          </div>
          <div>
            <Label htmlFor="med-session-notes" className="sr-only">Session notes</Label>
            <Textarea
              id="med-session-notes"
              rows={5}
              placeholder="Observations, skin condition, products used, home-care tips… the client will see this on their portal."
              value={record.session_notes}
              onChange={(e) => setRecord({ ...record, session_notes: e.target.value })}
              disabled={loading || saving}
              data-testid="medical-record-session-notes"
              aria-invalid={!notesFilled}
              className={!notesFilled ? "border-red-300 focus-visible:ring-red-200" : ""}
            />
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-2" data-testid="medical-attachments-hint">
              Attach compressed images (auto-shrunk to ≤{MAX_KB}KB, up to {MAX_ATTACHMENTS_PER_RECORD} per record).
              <span className="ml-1 text-emerald-700" data-testid="medical-consent-optional-hint">
                All attachments are optional.
              </span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {ATTACHMENT_KINDS.map(({ key, label, Icon, testid, required }) => {
                // Feb 2026 Global Validation Update: every attachment
                // slot (Informed Consent included) is optional. Never
                // apply a red "missing" style — the guest can save
                // with just session notes.
                const already = hasKind(key);
                return (
                  <Button
                    key={key}
                    type="button"
                    variant="outline"
                    className="min-h-11 h-auto py-2 px-2 justify-start items-center gap-2 whitespace-normal text-left leading-tight"
                    onClick={() => pickAndUpload(key)}
                    disabled={saving || loading || compressing === key}
                    data-testid={testid}
                    data-required="0"
                    data-satisfied={already ? "1" : "0"}
                  >
                    {compressing === key ? (
                      <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
                    ) : (
                      <Icon className={`w-4 h-4 shrink-0 ${already ? "text-primary" : "text-primary"}`} />
                    )}
                    <span className="text-xs leading-tight break-words flex-1">
                      Upload {label}
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>

          {record.attachments.length > 0 && (
            <div
              className="grid grid-cols-3 gap-2 pt-2"
              data-testid="medical-record-attachment-list"
            >
              {record.attachments.map((att, idx) => (
                <div
                  key={att.id || `new-${idx}`}
                  className="relative rounded-lg overflow-hidden border border-border bg-white group"
                  data-testid={`medical-record-attachment-${att.kind}-${idx}`}
                >
                  <img
                    src={att.data_url}
                    alt={kindLabel(att.kind)}
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-2 py-1 flex items-center justify-between">
                    <span className="truncate">{kindLabel(att.kind)}</span>
                    <span className="tabular-nums shrink-0">{((att.size || 0) / 1024).toFixed(0)}KB</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    disabled={saving}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 text-red-600 shadow flex items-center justify-center hover:bg-white"
                    aria-label={`Remove ${kindLabel(att.kind)}`}
                    data-testid={`medical-record-attachment-remove-${idx}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Section 2 — Private Internal Notes. Locked to Care Team +
            Management; never returned by any customer-facing API. */}
        <section
          className="mt-3 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-4 space-y-2"
          data-testid="medical-record-section-private"
        >
          <div className="flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-[11px] tracking-overline text-amber-800 uppercase font-semibold">Internal notes</span>
            <span className="text-[10px] rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5">Staff only · never shown to client</span>
          </div>
          <Label htmlFor="med-internal-notes" className="sr-only">Internal notes</Label>
          <Textarea
            id="med-internal-notes"
            rows={4}
            placeholder="Team-only observations, flags, safety concerns, referral notes…"
            value={record.internal_notes}
            onChange={(e) => setRecord({ ...record, internal_notes: e.target.value })}
            disabled={loading || saving}
            data-testid="medical-record-internal-notes"
          />
        </section>

        <DialogFooter className="mt-4 flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {!canDismiss && missing.length > 0 && (
            <p
              className="text-[11px] sm:text-xs text-red-700 sm:mr-auto"
              data-testid="medical-record-validation-hint"
            >
              Required to save: {missing.join(", ")}
            </p>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving || dismissing} data-testid="medical-record-cancel">Close</Button>
          {canDismiss ? (
            <Button
              variant="destructive"
              onClick={dismissTask}
              disabled={dismissing || loading}
              data-testid="medical-record-dismiss"
            >
              {dismissing ? "Clearing…" : "Dismiss / Clear Task"}
            </Button>
          ) : (
            <Button onClick={save} disabled={!canSave} data-testid="medical-record-save">
              {saving ? "Saving…" : "Save record"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
